import { SortingState } from "@tanstack/react-table";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Button, Card, Col, Container, Form, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { createSelector } from "reselect";

import { toast } from "react-toastify";
import {
  exportModulesCSV,
  fetchModules,
  ModulesxportsCSV,
  updateModuelsStatus,
} from "slices/modules/thunk";
import ModulesTableContainer from "./ModulesTableContainer";
import TableContainer from "Common/TableContainer";
import { app_modules } from "helpers/apps_modules_list";
import { fetchLatestPermissions } from "slices/auth/login/thunk";
import LoaderErrorWrapper from "Components/LoaderErrorWrapper/LoaderErrorWrapper";

const ModuleList = () => {
  document.title = "Module List";
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const selectModule = createSelector(
    (state: any) => state.modules,
    (mod) => ({
      modules: mod.modulesList,
      rowCount: mod.rowCount,
      loading: mod.loading,
      error: mod.error,
    })
  );

  const { modules, rowCount, loading, error } = useSelector(selectModule);

  const user = useSelector((state: any) => state.Login.user);
  const user_permissions = user?.permissions || [];
  const master_permissions = user_permissions?.find(
    (item: any) => item?.apps_name === app_modules?.user_management?.app_name
  );
  const module_permissions = master_permissions?.modules_permissions?.find(
    (item: any) =>
      item?.modules_name === app_modules?.user_management?.modules?.module
  );

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [columnFilters, setColumnFilters] = useState<any>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [colValue, setColValue] = useState<any>();
  const [filterOptions, setFilterOptions] = useState<Record<string, any[]>>({});

  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";

  useEffect(() => {
    dispatch(
      fetchModules({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? sortDirection : "",
        search,
        filters: columnFilters,
      })
    );
  }, [
    dispatch,
    pageIndex,
    pageSize,
    sorting,
    search,
    columnFilters,
    colValue,
    sortField,
    sortDirection,
  ]);

  // Reset to first page on filter, search, or page size change
  useEffect(() => {
    setPageIndex(0);
  }, [columnFilters, pageSize, search]);

  // Debounced search
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
      setPageIndex(0);
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [searchInput]);

  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const result = await dispatch(fetchLatestPermissions()).unwrap();
      } catch (error) {
        console.error("Failed to fetch latest permissions", error);
        // toast.error("Failed to fetch latest permissions");
      }
    };
    fetchPermissions();
  }, [dispatch]);

  // Handle status toggle
  const handleStatusToggle = useCallback(
    (module: any) => {
      const updatedStatus = !module?.status;
      const updatedModule = {
        ...module,
        status: updatedStatus,
      };

      dispatch(updateModuelsStatus(updatedModule))
        .unwrap()
        .then(() => {
          toast.success(
            `Module ${updatedStatus ? "activated" : "deactivated"} successfully`
          );
          dispatch(
            fetchModules({
              page: pageIndex + 1,
              pageSize,
              sort: sortField,
              order: sortField ? sortDirection : "",
              search,
              filters: columnFilters,
            })
          );
        })
        .catch((err: any) => {
          toast.error("Failed to update status");
          console.error(err);
        });
    },
    [
      dispatch,
      pageIndex,
      pageSize,
      sortField,
      sortDirection,
      search,
      columnFilters,
    ]
  );

  // const handleExport = async () => {
  //   const resultAction = await dispatch(exportModulesCSV());
  //   if (exportModulesCSV.fulfilled.match(resultAction)) {
  //     const { blob, filename } = resultAction.payload;
  //     const url = window.URL.createObjectURL(new Blob([blob]));
  //     const link = document.createElement("a");
  //     link.href = url;
  //     link.setAttribute("download", filename);
  //     document.body.appendChild(link);
  //     link.click();
  //     link.parentNode?.removeChild(link);
  //      window.URL.revokeObjectURL(url)
  //      toast.success('CSV exported successfully')
  //   } else {
  //     toast.error("Failed to export CSV");
  //   }
  // };

  const handleExport = async () => {
    try {
      const { blob, filename } = await ModulesxportsCSV({
        search,
        filters: columnFilters,
      });

      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("CSV exported successfully");
    } catch (error) {
      toast.error("Failed to export CSV");
    }
  };
  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const DynamicAction =
    // module_permissions?.permissions?.Update === true
    //   ?
    [
      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => (
          <ul className="d-flex gap-2 list-unstyled mb-0">
            <li>
              <span
                onClick={() => {
                  navigate(`/edit-module-form`, {
                    state: { ...row?.original },
                  });
                }}
                className="btn btn-subtle-secondary btn-icon btn-sm edit-item-btn"
              >
                <i className="ph-pencil"></i>
              </span>
            </li>
          </ul>
        ),
      },
    ];
  // : [];
  // Table columns
  const columns = useMemo(
    () => [
      // {
      //   header: "Sr. No.",
      //   id: "sr_no", // important: since there's no accessorKey
      //   enableSorting: false,
      //   enableColumnFilter: false,
      //   cell: ({ row }: { row: any }) => {
      //     return row.index + 1 + pageIndex * pageSize;
      //   },
      // },
      {
        header: "App Name",
        accessorKey: "app_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          "api/modulemaster/column-filter-dropdown/?column_filter=app_name",
      },

      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const mod = row.original;
          return (
            <div
              style={{ fontSize: "1.5rem", cursor: "pointer" }}
              onClick={() => handleStatusToggle(mod)}
            >
              <i
                className={`bi ${
                  mod.status
                    ? "bi-toggle-on text-primary"
                    : "bi-toggle-off text-danger"
                }`}
              />
            </div>
          );
        },
      },
      {
        header: "Created Date",
        accessorKey: "created_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const name = row.original.created_by_name ?? "N/A";
          const date = formatDate(row.original.created_at);
          return `  ${name}  -  ${date}  `; // extra spaces for visual padding
        },
      },
      {
        header: "Updated Date",
        accessorKey: "updated_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const name = row.original.updated_by_name ?? "N/A";
          const date = formatDate(row.original.updated_at);
          return `  ${name}  -  ${date}  `; // spaces before/after the name
        },
      },
      // {
      //   header: "Action",
      //   enableColumnFilter: false,
      //   enableSorting: false,
      //   cell: ({ row }: { row: any }) => (
      //     <ul className="d-flex gap-2 list-unstyled mb-0">
      //       <li>
      //         <span
      //           onClick={() => {
      //             navigate(`/edit-module-form`, {
      //               state: { ...row?.original },
      //             });
      //           }}
      //           className="btn btn-subtle-secondary btn-icon btn-sm edit-item-btn"
      //         >
      //           <i className="ph-pencil"></i>
      //         </span>
      //       </li>
      //     </ul>
      //   ),
      // },
      ...DynamicAction,
    ],
    [dispatch, modules, handleStatusToggle, module_permissions?.permissions]
  );

  return (
    <Fragment>
      <div className="page-content">
        <Container fluid>
          <Row id="invoiceList">
            <Col lg={12}>
              <Card>
                <Card.Header>
                  <Row className="align-items-center g-2">
                    <Col lg={3} className="me-auto">
                      <h6 className="card-title mb-0">Modules</h6>
                    </Col>
                    <Col xl={2} md={3}>
                      <div className="search-box">
                        <Form.Control
                          type="text"
                          className="search"
                          placeholder="Search"
                          value={searchInput}
                          onChange={(e: any) => setSearchInput(e.target.value)}
                        />
                        <i className="ri-search-line search-icon"></i>
                      </div>
                    </Col>
                    <Col className="col-md-auto">
                      <div className="hstack gap-2">
                        <Button
                          className="btn btn-subtle-danger d-none"
                          id="remove-actions"
                        >
                          <i className="ri-delete-bin-2-line"></i>
                        </Button>
                        {module_permissions?.permissions?.View === true && (
                          <div
                            className="hstack gap-2 btn btn-light"
                            onClick={handleExport}
                            style={{ cursor: "pointer" }}
                          >
                            <i className="bi bi-box-arrow-up align-baseline me-1"></i>{" "}
                            Export
                          </div>
                        )}
                        {/* { */}
                        {/* module_permissions?.permissions?.Create === true && */}
                        {/* {module_permissions?.permissions?.Create === true && ( */}
                          <Link to="/module-form" className="btn btn-primary">
                            <i className="bi bi-plus-circle align-baseline me-1"></i>
                            Add Module
                          </Link>
                        {/* )} */}
                      </div>
                    </Col>
                  </Row>
                </Card.Header>
                <Card.Body className="mt-3">
                  <LoaderErrorWrapper loading={loading} error={error}>
                    <TableContainer
                      columns={columns}
                      data={modules}
                      totalRecords={rowCount}
                      pageIndex={pageIndex}
                      pageSize={pageSize}
                      onPageChange={setPageIndex}
                      onPageSizeChange={setPageSize}
                      columnFilters={columnFilters}
                      setColumnFilters={setColumnFilters}
                      colValue={setColValue}
                      sorting={sorting}
                      onSortingChange={setSorting}
                      manualSorting={true}
                      isBordered={false}
                      isPagination={true}
                      isGlobalFilter={false}
                      //filterOptions={filterOptions}
                      SearchPlaceholder="Search modules..."
                    />
                  </LoaderErrorWrapper>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </Fragment>
  );
};

export default ModuleList;
